<script setup lang="ts">
import { ref, watch, onMounted } from 'vue'
import { Ellipsis, UserRound } from 'lucide-vue-next'
import axios from 'axios'
import { router } from '@inertiajs/vue3'

interface User {
  name: string
  avatar?: string
  is_author?: boolean
}

interface Reply {
  id: number
  user: User
  body: string
  created_at: string
  likes?: number
  dislikes?: number
}

interface Comment {
  id: number
  user: User
  body: string
  created_at: string
  likes?: number
  dislikes?: number
  replies?: Reply[]
}

interface PaginationLinks {
  url: string | null
  label: string
  active: boolean
}

interface PaginatedResponse {
  data: Comment[]
  current_page: number
  last_page: number
  per_page: number
  total: number
  links: PaginationLinks[]
}

interface Props {
  contentId: number
  initialCount?: number
  readonly?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  readonly: false,
})

const comments = ref<Comment[]>([])
const commentText = ref('')
const replyText = ref('')
const sortBy = ref('most_recent')
const replyingTo = ref<number | null>(null)
const loading = ref(false)
const submitting = ref(false)
const currentPage = ref(1)
const lastPage = ref(1)
const totalCount = ref(props.initialCount || 0)

const formatTimeAgo = (date: string) => {
  const now = new Date()
  const then = new Date(date)
  const seconds = Math.floor((now.getTime() - then.getTime()) / 1000)

  if (seconds < 60) return 'just now'
  if (seconds < 3600) return `${Math.floor(seconds / 60)} minute${Math.floor(seconds / 60) > 1 ? 's' : ''} ago`
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} hour${Math.floor(seconds / 3600) > 1 ? 's' : ''} ago`
  if (seconds < 604800) return `${Math.floor(seconds / 86400)} day${Math.floor(seconds / 86400) > 1 ? 's' : ''} ago`
  if (seconds < 2592000) return `${Math.floor(seconds / 604800)} week${Math.floor(seconds / 604800) > 1 ? 's' : ''} ago`
  return 'Published ' + then.toLocaleDateString()
}

/**
 * The photograph, or nothing — the template draws initials instead. Generating
 * a ui-avatars.com URL sent every commenter's name to a third party.
 */
const getAvatarUrl = (_name: string, avatar?: string) => avatar || null

const initialsOf = (name: string) => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean)
  return parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : (parts[0]?.[0] || '?').toUpperCase()
}

const fetchComments = async (page: number = 1) => {
  loading.value = true
  try {
    const response = await axios.get<PaginatedResponse>(`/library/${props.contentId}/comments`, {
      params: { page, sort: sortBy.value }
    })

    if (page === 1) {
      comments.value = response.data.data
    } else {
      comments.value.push(...response.data.data)
    }

    currentPage.value = response.data.current_page
    lastPage.value = response.data.last_page
    totalCount.value = response.data.total
  } catch (error) {
    console.error('Failed to fetch comments:', error)
  } finally {
    loading.value = false
  }
}

const postComment = async () => {
  if (!commentText.value.trim() || submitting.value) return

  submitting.value = true
  try {
    const response = await axios.post('/library/comments', {
      teacher_content_id: props.contentId,
      body: commentText.value,
      parent_id: null
    })

    // Add new comment to the top of the list
    comments.value.unshift(response.data)
    totalCount.value++
    commentText.value = ''
  } catch (error: any) {
    if (error.response?.status === 401) {
      alert('Please log in to post a comment')
      // Optionally redirect to login
    } else if (error.response?.status === 403) {
      alert(error.response?.data?.message || 'You are not authorized to post comments')
    } else {
      alert('Failed to post comment. Please try again.')
      console.error('Failed to post comment:', error)
    }
  } finally {
    submitting.value = false
  }
}

const openReply = (commentId: number) => {
  replyingTo.value = commentId
  replyText.value = ''
}

const cancelReply = () => {
  replyingTo.value = null
  replyText.value = ''
}

const postReply = async (commentId: number) => {
  if (!replyText.value.trim() || submitting.value) return

  submitting.value = true
  try {
    const response = await axios.post('/library/comments', {
      teacher_content_id: props.contentId,
      body: replyText.value,
      parent_id: commentId
    })

    // Find the parent comment and add the reply
    const parentComment = comments.value.find(c => c.id === commentId)
    if (parentComment) {
      if (!parentComment.replies) {
        parentComment.replies = []
      }
      parentComment.replies.push(response.data)
      totalCount.value++
    }

    cancelReply()
  } catch (error: any) {
    if (error.response?.status === 401) {
      alert('Please log in to post a reply')
    } else if (error.response?.status === 403) {
      alert(error.response?.data?.message || 'You are not authorized to post replies')
    } else {
      alert('Failed to post reply. Please try again.')
      console.error('Failed to post reply:', error)
    }
  } finally {
    submitting.value = false
  }
}

const loadMore = () => {
  if (currentPage.value < lastPage.value && !loading.value) {
    fetchComments(currentPage.value + 1)
  }
}

watch(sortBy, () => {
  fetchComments(1)
})

onMounted(() => {
  fetchComments()
})
</script>

<template>
  <section id="comments-section" class="bg-white rounded-xl border border-gray-200 p-6">
    <div class="flex items-center justify-between mb-6">
      <h3 class="text-xl font-bold text-gray-900">
        Comments <span class="text-gray-500 font-normal">({{ totalCount }})</span>
      </h3>
      <select
        v-model="sortBy"
        class="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue"
      >
        <option value="most_recent">Most Recent</option>
        <option value="top_comments">Top Comments</option>
        <option value="oldest_first">Oldest First</option>
      </select>
    </div>

    <!-- Add Comment -->
    <div v-if="!readonly" id="add-comment" class="mb-6">
      <div class="flex items-start gap-3">
        <span
          class="grid w-10 h-10 shrink-0 place-items-center rounded-full bg-surface-2 text-subtle"
          aria-hidden="true"
        >
          <UserRound class="size-5" />
        </span>
        <div class="flex-1">
          <textarea
            v-model="commentText"
            placeholder="Add a comment..."
            class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue resize-none"
            rows="3"
            :disabled="submitting"
          />
          <div class="flex justify-end mt-3">
            <button
              @click="postComment"
              :disabled="submitting || !commentText.trim()"
              class="px-4 py-2 bg-brand-blue text-white rounded-lg font-semibold text-sm hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {{ submitting ? 'Posting...' : 'Post' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading && currentPage === 1" class="text-center py-8">
      <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand-blue" />
      <p class="mt-2 text-gray-600">Loading comments...</p>
    </div>

    <!-- Comments List -->
    <div v-else id="comments-list" class="space-y-6">
      <div v-for="comment in comments" :key="comment.id">
        <div class="flex items-start gap-3">
          <img
            v-if="getAvatarUrl(comment.user.name, comment.user.avatar)"
            :src="getAvatarUrl(comment.user.name, comment.user.avatar)"
            :alt="comment.user.name"
            class="w-10 h-10 rounded-full border-2 border-gray-200 object-cover"
          />
          <span
            v-else
            class="grid w-10 h-10 shrink-0 place-items-center rounded-full bg-brand-soft text-sm font-semibold text-brand-soft-text"
            :aria-label="comment.user.name"
            role="img"
          >{{ initialsOf(comment.user.name) }}</span>
          <div class="flex-1">
            <div :class="[comment.user.is_author ? 'bg-blue-50' : 'bg-gray-50', 'rounded-lg p-4']">
              <div class="flex items-center justify-between mb-2">
                <div class="flex items-center gap-2">
                  <span class="font-semibold text-gray-900 text-sm">{{ comment.user.name }}</span>
                  <span
                    v-if="comment.user.is_author"
                    class="bg-blue-600 text-white text-xs px-2 py-0.5 rounded"
                  >
                    Tutor
                  </span>
                  <span class="text-gray-500 text-xs">{{ formatTimeAgo(comment.created_at) }}</span>
                </div>
                <button class="text-gray-400 hover:text-gray-600">
                  <Ellipsis class="w-5 h-5" />
                </button>
              </div>
              <p class="text-gray-700 text-sm leading-relaxed">{{ comment.body }}</p>
            </div>
            <div v-if="!readonly" class="flex items-center gap-4 mt-2 ml-4">
              <button
                @click="openReply(comment.id)"
                class="text-gray-600 hover:text-gray-900 text-sm font-medium"
              >
                Reply
              </button>
            </div>

            <!-- Reply Input -->
            <div v-if="!readonly && replyingTo === comment.id" class="mt-4 flex items-start gap-3">
              <span
                class="grid w-8 h-8 shrink-0 place-items-center rounded-full bg-surface-2 text-subtle"
                aria-hidden="true"
              >
                <UserRound class="size-4" />
              </span>
              <div class="flex-1">
                <textarea
                  v-model="replyText"
                  placeholder="Write a reply..."
                  class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue resize-none text-sm"
                  rows="2"
                  :disabled="submitting"
                />
                <div class="flex items-center justify-end gap-2 mt-2">
                  <button
                    @click="cancelReply"
                    :disabled="submitting"
                    class="px-3 py-1.5 text-gray-600 hover:text-gray-800 font-medium text-sm disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    @click="postReply(comment.id)"
                    :disabled="submitting || !replyText.trim()"
                    class="px-3 py-1.5 bg-brand-blue text-white rounded-lg font-semibold text-sm hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {{ submitting ? 'Posting...' : 'Reply' }}
                  </button>
                </div>
              </div>
            </div>

            <!-- Replies -->
            <div v-if="comment.replies && comment.replies.length > 0" class="mt-4 space-y-4 pl-4 border-l-2 border-gray-200 ml-6">
              <div v-for="reply in comment.replies" :key="reply.id" class="flex items-start gap-3">
                <img
                  v-if="getAvatarUrl(reply.user.name, reply.user.avatar)"
                  :src="getAvatarUrl(reply.user.name, reply.user.avatar)"
                  :alt="reply.user.name"
                  class="w-8 h-8 rounded-full border-2 border-gray-200 object-cover"
                />
                <span
                  v-else
                  class="grid w-8 h-8 shrink-0 place-items-center rounded-full bg-brand-soft text-xs font-semibold text-brand-soft-text"
                  :aria-label="reply.user.name"
                  role="img"
                >{{ initialsOf(reply.user.name) }}</span>
                <div class="flex-1">
                  <div :class="[reply.user.is_author ? 'bg-blue-50' : 'bg-gray-50', 'rounded-lg p-3']">
                    <div class="flex items-center justify-between mb-2">
                      <div class="flex items-center gap-2">
                        <span class="font-semibold text-gray-900 text-sm">{{ reply.user.name }}</span>
                        <span
                          v-if="reply.user.is_author"
                          class="bg-blue-600 text-white text-xs px-2 py-0.5 rounded"
                        >
                          Tutor
                        </span>
                        <span class="text-gray-500 text-xs">{{ formatTimeAgo(reply.created_at) }}</span>
                      </div>
                    </div>
                    <p class="text-gray-700 text-sm leading-relaxed">{{ reply.body }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="comments.length === 0 && !loading" class="text-center py-8">
        <p class="text-gray-600">{{ readonly ? 'No comments yet.' : 'No comments yet. Be the first to comment!' }}</p>
      </div>
    </div>

    <!-- Load More Button -->
    <button
      v-if="currentPage < lastPage"
      @click="loadMore"
      :disabled="loading"
      class="w-full mt-6 py-3 border-2 border-gray-200 rounded-lg text-gray-700 font-semibold hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {{ loading ? 'Loading...' : 'Load More Comments' }}
    </button>
  </section>
</template>
